// React imports
import { useState, useEffect, useRef } from 'react';

// Other imports
import axios from 'axios';
import dayjs from 'dayjs';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useTheme, alpha } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faArrowUp, faArrowDown, faMagnifyingGlass, faCircleInfo, faTriangleExclamation, faHourglassHalf } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import { Pill } from './ElariomParts.js';

// The log of one agent run, in a side panel: what was sent to Claude, what came back, each
// place lookup, and progress while a long answer is written. It follows a running run live
// and can stop it. Recorded by `trace` in src/server/rateAgent/agent.js.

const STEP_LABEL = { classify: 'Classify', parse: 'Parse rates', read: 'Read the file', build: 'Build form & rates', validate: 'Check with importer', start: 'Start', end: 'End', stop: 'Stop' };

function Collapsible({ title, text, open: openByDefault = false, mono = true }) {
    const theme = useTheme();
    const [ open, setOpen ] = useState(openByDefault);
    return (
        <Box sx={{ borderTop: `1px solid ${theme.palette.border1}`, paddingTop: '4px' }}>
            <Box role='button' tabIndex={0} onClick={() => setOpen(v => !v)} onKeyDown={e => e.key === 'Enter' && setOpen(v => !v)}
                sx={{ fontSize: '12px', color: theme.palette.primary.main, cursor: 'pointer', userSelect: 'none' }}>
                {open ? '▾' : '▸'} {title}
            </Box>
            {open && (
                <Box component='pre' sx={{
                    margin: '4px 0 0', padding: '8px', fontSize: '11.5px', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    background: alpha(theme.palette.text.secondary, 0.06), borderRadius: '6px', maxHeight: '420px', overflow: 'auto',
                    fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
                }}>
                    {text}
                </Box>
            )}
        </Box>
    );
}

function Blocks({ blocks }) {
    return (blocks || []).map((b, i) => {
        if (b.type === 'text') return <Collapsible key={i} title={`Text (${b.text.length.toLocaleString()} characters)`} text={b.text} open={b.text.length < 1500} mono={false} />;
        if (b.type === 'document' || b.type === 'image') return <Typography key={i} sx={{ fontSize: '12px', padding: '4px 0' }}>📎 {b.summary}</Typography>;
        if (b.type === 'tool_use') return <Collapsible key={i} title={`Answer through the tool "${b.name}" (${b.input.length.toLocaleString()} characters)`} text={b.input} open={b.input.length < 1500} />;
        if (b.type === 'tool_result') return <Collapsible key={i} title={`Result sent back (${b.content.length.toLocaleString()} characters)`} text={b.content} />;
        if (b.type === 'thinking') return <Collapsible key={i} title='Thinking' text={b.text} />;
        return <Typography key={i} sx={{ fontSize: '12px' }}>[{b.type}]</Typography>;
    });
}

function Event({ e }) {
    const theme = useTheme();
    const time = dayjs(e.at).format('HH:mm:ss');
    const step = STEP_LABEL[e.step] || e.step;
    const card = (color, icon, title, children) => (
        <Box sx={{ borderLeft: `3px solid ${color}`, background: theme.palette.panel1, border: `1px solid ${theme.palette.border1}`, borderLeftWidth: '3px', borderLeftColor: color, borderRadius: '6px', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <FontAwesomeIcon icon={icon} style={{ color, fontSize: '12px' }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{title}</Typography>
                <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary, marginLeft: 'auto' }}>{time}</Typography>
            </Box>
            {children}
        </Box>
    );

    if (e.type === 'stage') {
        return (
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'baseline', fontSize: '12px', color: theme.palette.text.secondary, padding: '2px 4px' }}>
                <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: '11px' }} />
                <span><b>{step}:</b> {e.text}</span>
                <span style={{ marginLeft: 'auto', fontSize: '11px' }}>{time}</span>
            </Box>
        );
    }
    if (e.type === 'progress') {
        return (
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'baseline', fontSize: '12px', color: theme.palette.text.secondary, padding: '0 4px 0 18px' }}>
                <FontAwesomeIcon icon={faHourglassHalf} style={{ fontSize: '10px' }} />
                <span>Claude is still writing the answer: {e.seconds}s, {Number(e.characters || 0).toLocaleString()} characters so far.</span>
            </Box>
        );
    }
    if (e.type === 'request') {
        return card(theme.palette.primary.main, faArrowUp, `Sent to Claude · ${step}, round ${e.round}`, (
            <>
                <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                    Model {e.model} · up to {Number(e.maxTokens).toLocaleString()} tokens in the reply · tools: {(e.tools || []).join(', ')} · {e.toolChoice}
                </Typography>
                {e.note && <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>{e.note}</Typography>}
                {(e.system || []).map((b, i) => <Collapsible key={`s${i}`} title={`Instructions and rules (system prompt, part ${i + 1}: ${b.text.length.toLocaleString()} characters)`} text={b.text} mono={false} />)}
                {(e.messages || []).map((m, i) => (
                    <Box key={`m${i}`}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '4px' }}>
                            {m.role === 'user' ? 'Message to Claude' : 'Claude\'s earlier turn'}
                        </Typography>
                        <Blocks blocks={m.content} />
                    </Box>
                ))}
            </>
        ));
    }
    if (e.type === 'response') {
        const u = e.usage || {};
        const tokensIn = (u.input_tokens || 0) + (u.cache_read_input_tokens || 0) + (u.cache_creation_input_tokens || 0);
        return card(theme.palette.success.main, faArrowDown, `Reply from Claude · ${step}, round ${e.round}`, (
            <>
                <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                    {e.seconds}s · {tokensIn.toLocaleString()} tokens in{u.cache_read_input_tokens ? ` (${u.cache_read_input_tokens.toLocaleString()} read from cache)` : ''} · {(u.output_tokens || 0).toLocaleString()} tokens out · ended because: {e.stopReason}
                </Typography>
                <Blocks blocks={e.content} />
            </>
        ));
    }
    if (e.type === 'tool') {
        return card(theme.palette.info.main, faMagnifyingGlass, `${e.name === 'lookup_locations' ? 'Looked up place codes' : `Ran ${e.name}`} · ${step}, round ${e.round}`, (
            <>
                <Collapsible title={`What Claude asked for (${e.input.length.toLocaleString()} characters)`} text={e.input} />
                <Collapsible title={`What was found (${e.result.length.toLocaleString()} characters)`} text={e.result} />
            </>
        ));
    }
    if (e.type === 'error') {
        return card(theme.palette.error.main, faTriangleExclamation, `Error · ${step}`, <Typography sx={{ fontSize: '12px' }}>{e.message || e.text}</Typography>);
    }
    return null;
}

function ElariomRunLog({ org, accesstoken, drop, run, onClose, onStopped }) {
    const theme = useTheme();
    const [ events, setEvents ] = useState([]);
    const [ done, setDone ] = useState(false);
    const [ missing, setMissing ] = useState(false);
    const [ stopping, setStopping ] = useState(false);
    const [ error, setError ] = useState(null);
    const bottom = useRef(null);
    const scroller = useRef(null);
    const count = useRef(0);

    useEffect(() => {
        let cancelled = false;
        let timer = null;
        const poll = async () => {
            try {
                const { data } = await axios.get(`/api/admin/elariom/${org.id}/drops/${drop.id}/runs/${run.id}/log?since=${count.current}&accesstoken=${accesstoken}`);
                if (cancelled) return;
                if (data.events?.length) {
                    count.current += data.events.length;
                    // Follow the end of the log only if the reader is already at the end.
                    const el = scroller.current;
                    const atEnd = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 80;
                    setEvents(prev => [...prev, ...data.events]);
                    if (atEnd) setTimeout(() => bottom.current?.scrollIntoView({ block: 'end' }), 50);
                }
                setMissing(!!data.missing);
                setDone(data.done);
                if (!data.done) timer = setTimeout(poll, 2000);
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.detail || e.message);
            }
        };
        poll();
        return () => { cancelled = true; clearTimeout(timer); };
    }, [org.id, drop.id, run.id, accesstoken]);

    const stop = async () => {
        setStopping(true);
        setError(null);
        try {
            await axios.post(`/api/admin/elariom/${org.id}/drops/${drop.id}/runs/${run.id}/stop?accesstoken=${accesstoken}`);
            onStopped?.();
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
            setStopping(false);
        }
    };

    const running = run.status === 'running' && !done;
    const requests = events.filter(e => e.type === 'request').length;
    const tokens = events.filter(e => e.type === 'response').reduce((acc, e) => ({
        in: acc.in + (e.usage?.input_tokens || 0) + (e.usage?.cache_read_input_tokens || 0) + (e.usage?.cache_creation_input_tokens || 0),
        out: acc.out + (e.usage?.output_tokens || 0),
    }), { in: 0, out: 0 });

    return (
        <Drawer anchor='right' open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: '640px' }, background: theme.palette.background.default } }}>
            <Box sx={{ padding: '14px 16px', borderBottom: `1px solid ${theme.palette.border1}`, background: theme.palette.panel1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Agent run log</Typography>
                        <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, overflowWrap: 'anywhere' }}>
                            {run.fileName} · started {dayjs(run.at).format('MMM D, HH:mm:ss')}{run.by ? ` by ${run.by}` : ''}
                        </Typography>
                    </Box>
                    <Button size='small' onClick={onClose} sx={{ minWidth: 0, color: theme.palette.text.secondary }} aria-label='Close the log'>
                        <FontAwesomeIcon icon={faXmark} />
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {running
                        ? <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}><CircularProgress size={12} /> Running</Box>
                        : <Pill color={run.status === 'done' ? theme.palette.success.main : run.status === 'stopped' ? theme.palette.text.secondary : theme.palette.error.main}>
                            {run.status === 'done' ? 'Finished' : run.status === 'stopped' ? 'Stopped' : run.status === 'failed' ? 'Failed' : 'Finished'}
                        </Pill>}
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                        {requests} request{requests === 1 ? '' : 's'} to Claude · {tokens.in.toLocaleString()} tokens in · {tokens.out.toLocaleString()} tokens out so far
                    </Typography>
                    {running && (
                        <Button size='small' variant='outlined' color='error' onClick={stop} disabled={stopping} sx={{ textTransform: 'none', marginLeft: 'auto' }}>
                            {stopping ? 'Stopping…' : 'Stop this run'}
                        </Button>
                    )}
                </Box>
                {running && (
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                        Stopping cancels the request to Claude right away. Nothing has been saved to the workspace, so nothing needs undoing.
                    </Typography>
                )}
                {error && <Alert severity='error' sx={{ fontSize: '12px', py: 0 }}>{error}</Alert>}
            </Box>
            <Box ref={scroller} sx={{ flexGrow: 1, overflow: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {missing && !events.length && (
                    <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                        This run has no log. Logs are kept for runs started after the log was added (1 Oct 2026), and a run cut off by a server restart may have saved only part of it.
                    </Typography>
                )}
                {!missing && !events.length && <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>Waiting for the first entry…</Typography>}
                {events.map((e, i) => <Event key={i} e={e} />)}
                <div ref={bottom} />
            </Box>
        </Drawer>
    );
}

export default ElariomRunLog;
